import React, { useState } from "react";
import { Team } from "../types/tournament";
import { LeagueCreationOptions } from "../types/league";
import {
  Users,
  Calendar,
  Target,
  Crown,
  TrendingUp,
  Clock,
  Star,
  ArrowLeft,
  Sparkles,
  Plus,
} from "lucide-react";

interface LeagueSelectionProps {
  onBack: () => void;
  onSelectLeague: (options: LeagueCreationOptions) => void;
}

const LeagueSelection: React.FC<LeagueSelectionProps> = ({
  onBack,
  onSelectLeague,
}) => {
  const [selectedLeague, setSelectedLeague] = useState<string | null>(null);
  const [leagueName, setLeagueName] = useState("");
  const [teams, setTeams] = useState<Team[]>([]);
  const [newTeamName, setNewTeamName] = useState("");
  const [newPlayer1, setNewPlayer1] = useState("");
  const [newPlayer2, setNewPlayer2] = useState("");
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [showAutoGenerate, setShowAutoGenerate] = useState(false);
  const [autoGenerateCount, setAutoGenerateCount] = useState(8);

  const leagueFormats = [
    {
      id: "round_robin",
      name: "Traditional Round Robin League",
      description:
        "Everyone plays everyone in a structured schedule. Perfect for clubs wanting balanced competition.",
      icon: <Users className="w-6 h-6" />,
      color: "blue",
      optimalTeams: "6-10 teams",
      duration: "5-18 weeks",
      features: [
        "Fixed weekly schedule",
        "Win=3, Close loss=1, Loss=0",
        "Balanced competition",
        "Clear standings",
      ],
      minTeams: 6,
      maxTeams: 12,
    },
    {
      id: "division",
      name: "Division League",
      description:
        "Skill-based divisions with intra-division focus and playoffs. Great for larger clubs with mixed skill levels.",
      icon: <Target className="w-6 h-6" />,
      color: "green",
      optimalTeams: "12-24 teams",
      duration: "10-12 weeks",
      features: [
        "2-4 skill divisions",
        "Intra-division play",
        "Playoff system",
        "Promotion/relegation",
      ],
      minTeams: 12,
      maxTeams: 32,
    },
    {
      id: "ladder",
      name: "Ladder League (Continuous)",
      description:
        "Flexible challenge system with ongoing competition. Teams challenge each other to move up the ladder.",
      icon: <TrendingUp className="w-6 h-6" />,
      color: "purple",
      optimalTeams: "Unlimited teams",
      duration: "Ongoing",
      features: [
        "Challenge system",
        "Flexible scheduling",
        "Monthly champions",
        "Continuous play",
      ],
      minTeams: 4,
      maxTeams: 100,
    },
    {
      id: "flex",
      name: "Flex League",
      description:
        "Self-scheduled matches with complete X matches in Y weeks. Perfect for busy players.",
      icon: <Clock className="w-6 h-6" />,
      color: "orange",
      optimalTeams: "8-16 teams",
      duration: "6-8 weeks",
      features: [
        "Self-scheduling",
        "Match requirements",
        "Flexible timing",
        "App coordination",
      ],
      minTeams: 6,
      maxTeams: 20,
    },
    {
      id: "hybrid",
      name: "Hybrid Progressive League",
      description:
        "Three-phase progression: Pool play → Ladder format → Playoffs. Dynamic competition structure.",
      icon: <Star className="w-6 h-6" />,
      color: "indigo",
      optimalTeams: "12-20 teams",
      duration: "8-10 weeks",
      features: [
        "Three phases",
        "Progressive format",
        "Playoff finale",
        "Dynamic competition",
      ],
      minTeams: 8,
      maxTeams: 24,
    },
    {
      id: "king_of_court",
      name: "King of the Court League",
      description:
        "Weekly 2-hour sessions with live ladder movement. Points by court position.",
      icon: <Crown className="w-6 h-6" />,
      color: "yellow",
      optimalTeams: "12-20 teams",
      duration: "8-10 weeks",
      features: [
        "Live ladder movement",
        "Court-based scoring",
        "Weekly sessions",
        "Dynamic rankings",
      ],
      minTeams: 8,
      maxTeams: 24,
    },
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      blue: "border-blue-200 bg-blue-50 hover:border-blue-400",
      green: "border-green-200 bg-green-50 hover:border-green-400",
      purple: "border-purple-200 bg-purple-50 hover:border-purple-400",
      orange: "border-orange-200 bg-orange-50 hover:border-orange-400",
      indigo: "border-indigo-200 bg-indigo-50 hover:border-indigo-400",
      yellow: "border-yellow-200 bg-yellow-50 hover:border-yellow-400",
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  const getIconColor = (color: string) => {
    const colors = {
      blue: "text-blue-600",
      green: "text-green-600",
      purple: "text-purple-600",
      orange: "text-orange-600",
      indigo: "text-indigo-600",
      yellow: "text-yellow-600",
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  const addTeam = () => {
    if (newTeamName && newPlayer1 && newPlayer2) {
      const team: Team = {
        id: Date.now().toString(),
        name: newTeamName,
        player1: newPlayer1,
        player2: newPlayer2,
        wins: 0,
        losses: 0,
        currentCourt: 1,
        startingCourt: 1,
        totalMovement: 0,
        achievements: [],
        court1Streak: 0,
        perfectGames: 0,
      };
      setTeams([...teams, team]);
      setNewTeamName("");
      setNewPlayer1("");
      setNewPlayer2("");
      setShowAddTeam(false);
    }
  };

  const removeTeam = (teamId: string) => {
    setTeams(teams.filter((team) => team.id !== teamId));
  };

  // Auto-generate team names and player names
  const generateTeamName = () => {
    const teamNames = [
      "Thunder",
      "Lightning",
      "Storm",
      "Hurricane",
      "Tornado",
      "Cyclone",
      "Eagles",
      "Hawks",
      "Falcons",
      "Phoenix",
      "Dragons",
      "Lions",
      "Tigers",
      "Bears",
      "Wolves",
      "Sharks",
      "Dolphins",
      "Whales",
      "Rockets",
      "Stars",
      "Comets",
      "Meteors",
      "Galaxy",
      "Nebula",
      "Warriors",
      "Knights",
      "Crusaders",
      "Gladiators",
      "Spartans",
      "Vikings",
      "Pirates",
      "Ninjas",
      "Samurai",
      "Archers",
      "Mages",
      "Paladins",
      "Rangers",
      "Scouts",
      "Hunters",
      "Trappers",
      "Trackers",
      "Guides",
      "Champions",
      "Heroes",
      "Legends",
      "Myths",
      "Tales",
      "Sagas",
      "Dynasty",
      "Empire",
      "Kingdom",
      "Realm",
      "Domain",
      "Territory",
    ];
    return teamNames[Math.floor(Math.random() * teamNames.length)];
  };

  const generatePlayerName = () => {
    const firstNames = [
      "Alex",
      "Jordan",
      "Casey",
      "Taylor",
      "Morgan",
      "Riley",
      "Avery",
      "Quinn",
      "Blake",
      "Drew",
      "Hayden",
      "Parker",
      "Cameron",
      "Jamie",
      "Dakota",
      "Reese",
      "Peyton",
      "Rowan",
      "Sage",
      "River",
      "Skyler",
      "Phoenix",
      "Ember",
      "Storm",
      "Hunter",
      "Fisher",
      "Mason",
      "Carter",
      "Cooper",
      "Walker",
      "Bennett",
      "Harrison",
      "Mitchell",
      "Parker",
      "Spencer",
      "Tyler",
      "Zoe",
      "Ava",
      "Mia",
      "Emma",
      "Sophia",
      "Isabella",
      "Olivia",
      "Charlotte",
      "Amelia",
      "Harper",
      "Evelyn",
      "Abigail",
      "Emily",
      "Elizabeth",
      "Sofia",
      "Avery",
      "Ella",
      "Madison",
    ];

    const lastNames = [
      "Smith",
      "Johnson",
      "Williams",
      "Brown",
      "Jones",
      "Garcia",
      "Miller",
      "Davis",
      "Rodriguez",
      "Martinez",
      "Hernandez",
      "Lopez",
      "Gonzalez",
      "Wilson",
      "Anderson",
      "Thomas",
      "Taylor",
      "Moore",
      "Jackson",
      "Martin",
      "Lee",
      "Perez",
      "Thompson",
      "White",
      "Harris",
      "Sanchez",
      "Clark",
      "Ramirez",
      "Lewis",
      "Robinson",
      "Walker",
      "Young",
      "Allen",
      "King",
      "Wright",
      "Scott",
      "Torres",
      "Nguyen",
      "Hill",
      "Flores",
      "Green",
      "Adams",
      "Nelson",
      "Baker",
      "Hall",
      "Rivera",
      "Campbell",
      "Mitchell",
    ];

    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    return `${firstName} ${lastName}`;
  };

  const autoGenerateTeams = () => {
    const newTeams: Team[] = [];
    const existingTeamNames = new Set(teams.map((team) => team.name));

    for (let i = 0; i < autoGenerateCount; i++) {
      let teamName = generateTeamName();
      let attempts = 0;

      // Ensure unique team names
      while (existingTeamNames.has(teamName) && attempts < 50) {
        teamName = generateTeamName();
        attempts++;
      }

      existingTeamNames.add(teamName);

      const team: Team = {
        id: Date.now().toString() + i,
        name: teamName,
        player1: generatePlayerName(),
        player2: generatePlayerName(),
        wins: 0,
        losses: 0,
        currentCourt: 1,
        startingCourt: 1,
        totalMovement: 0,
        achievements: [],
        court1Streak: 0,
        perfectGames: 0,
      };

      newTeams.push(team);
    }

    setTeams([...teams, ...newTeams]);
    setShowAutoGenerate(false);
  };

  const handleCreateLeague = () => {
    if (selectedLeague && leagueName && teams.length >= 2) {
      const selectedFormat = leagueFormats.find((f) => f.id === selectedLeague);

      if (
        selectedFormat &&
        teams.length >= selectedFormat.minTeams &&
        teams.length <= selectedFormat.maxTeams
      ) {
        const options: LeagueCreationOptions = {
          name: leagueName,
          teams: teams,
          leagueType: selectedLeague as any,
          settings: {
            totalWeeks: selectedFormat.id === "ladder" ? 12 : 10,
            seasonStartDate: new Date(),
            seasonEndDate: new Date(Date.now() + 10 * 7 * 24 * 60 * 60 * 1000), // 10 weeks from now
          },
        };
        onSelectLeague(options);
      }
    }
  };

  const selectedFormat = leagueFormats.find((f) => f.id === selectedLeague);
  const canCreate =
    selectedLeague &&
    leagueName &&
    teams.length >= 2 &&
    selectedFormat &&
    teams.length >= selectedFormat.minTeams &&
    teams.length <= selectedFormat.maxTeams;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Create League</h1>
            <div className="w-20"></div> {/* Spacer */}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* League Format Selection */}
          <div className="lg:col-span-2">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Choose League Format
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {leagueFormats.map((format) => (
                <div
                  key={format.id}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                    selectedLeague === format.id
                      ? "ring-2 ring-blue-500 border-blue-500"
                      : getColorClasses(format.color)
                  }`}
                  onClick={() => setSelectedLeague(format.id)}
                >
                  <div className="flex items-start mb-3">
                    <div
                      className={`p-2 rounded-full mr-3 ${getColorClasses(
                        format.color
                      )
                        .replace("border-", "bg-")
                        .replace("hover:border-", "")}`}
                    >
                      <div className={getIconColor(format.color)}>
                        {format.icon}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {format.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        {format.description}
                      </p>
                      <div className="flex items-center text-xs text-gray-500 mb-2">
                        <Users className="w-3 h-3 mr-1" />
                        <span>{format.optimalTeams}</span>
                        <span className="mx-2">•</span>
                        <Calendar className="w-3 h-3 mr-1" />
                        <span>{format.duration}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    {format.features.map((feature, index) => (
                      <div
                        key={index}
                        className="text-xs text-gray-600 flex items-center"
                      >
                        <div className="w-1 h-1 bg-gray-400 rounded-full mr-2"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* League Setup */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                League Setup
              </h2>

              {/* League Name */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  League Name
                </label>
                <input
                  type="text"
                  value={leagueName}
                  onChange={(e) => setLeagueName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter league name"
                />
              </div>

              {/* Selected Format Info */}
              {selectedFormat && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {selectedFormat.name}
                  </h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>Optimal: {selectedFormat.optimalTeams}</div>
                    <div>Duration: {selectedFormat.duration}</div>
                    <div>
                      Teams: {teams.length} / {selectedFormat.maxTeams}
                    </div>
                  </div>
                </div>
              )}

              {/* Teams */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Teams ({teams.length})
                  </label>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setShowAutoGenerate(true)}
                      className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 flex items-center"
                    >
                      <Sparkles className="w-3 h-3 mr-1" />
                      Auto Generate
                    </button>
                    <button
                      onClick={() => setShowAddTeam(true)}
                      className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 flex items-center"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Team
                    </button>
                  </div>
                </div>

                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {teams.map((team) => (
                    <div
                      key={team.id}
                      className="flex justify-between items-center p-2 bg-gray-50 rounded"
                    >
                      <div>
                        <div className="font-medium text-sm">{team.name}</div>
                        <div className="text-xs text-gray-600">
                          {team.player1} & {team.player2}
                        </div>
                      </div>
                      <button
                        onClick={() => removeTeam(team.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Create Button */}
              <button
                onClick={handleCreateLeague}
                disabled={!canCreate}
                className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors duration-200 ${
                  canCreate
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Create League
              </button>
            </div>
          </div>
        </div>

        {/* Add Team Modal */}
        {showAddTeam && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Add Team
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team Name
                  </label>
                  <input
                    type="text"
                    value={newTeamName}
                    onChange={(e) => setNewTeamName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter team name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Player 1
                  </label>
                  <input
                    type="text"
                    value={newPlayer1}
                    onChange={(e) => setNewPlayer1(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter player 1 name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Player 2
                  </label>
                  <input
                    type="text"
                    value={newPlayer2}
                    onChange={(e) => setNewPlayer2(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter player 2 name"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowAddTeam(false)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={addTeam}
                  disabled={!newTeamName || !newPlayer1 || !newPlayer2}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Add Team
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Auto Generate Teams Modal */}
        {showAutoGenerate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center mb-4">
                <Sparkles className="w-5 h-5 text-purple-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Auto Generate Teams
                </h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Teams to Generate
                  </label>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() =>
                        setAutoGenerateCount(Math.max(1, autoGenerateCount - 1))
                      }
                      className="p-2 rounded-md bg-gray-100 hover:bg-gray-200"
                    >
                      -
                    </button>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={autoGenerateCount}
                      onChange={(e) =>
                        setAutoGenerateCount(parseInt(e.target.value) || 1)
                      }
                      className="w-20 text-center border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      onClick={() =>
                        setAutoGenerateCount(
                          Math.min(20, autoGenerateCount + 1)
                        )
                      }
                      className="p-2 rounded-md bg-gray-100 hover:bg-gray-200"
                    >
                      +
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Range: 1-20 teams
                  </p>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-medium text-purple-900 mb-2">
                    What will be generated:
                  </h4>
                  <ul className="text-sm text-purple-800 space-y-1">
                    <li>
                      • Unique team names (Thunder, Eagles, Warriors, etc.)
                    </li>
                    <li>• Random player names with first and last names</li>
                    <li>• Teams ready for league competition</li>
                    <li>• No duplicate team names</li>
                  </ul>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowAutoGenerate(false)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={autoGenerateTeams}
                  className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 flex items-center justify-center"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate {autoGenerateCount} Teams
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LeagueSelection;
